import { type FC, Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { MetaAdsRole } from "shared";
import { Icon } from "../common/Icon";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { useMetaAdsRoles, useMetaAdsRolesActions } from "./useMetaAdsRoles";
import { useCanManageMetaAdsRoles } from "./useMyMetaAdsRole";

const roleLabels: Record<MetaAdsRole, string> = {
	[MetaAdsRole.VIEWER]: "Viewer",
	[MetaAdsRole.EDITOR]: "Editor",
	[MetaAdsRole.MANAGER]: "Manager",
};

const MetaAdsRoleManagerInner: FC = () => {
	const company = useCompany();
	const { data: roles } = useMetaAdsRoles();
	const { setRole, removeRole } = useMetaAdsRolesActions();

	const [addUserId, setAddUserId] = useState<number | "">("");
	const [addRole, setAddRole] = useState<MetaAdsRole>(MetaAdsRole.VIEWER);

	const assignedUserIds = new Set(roles.map((r) => r.userId));
	const unassignedUsers = company.users.filter(
		(u) => !assignedUserIds.has(u.id),
	);

	return (
		<Card>
			<Card.Header>Meta Ads Roles</Card.Header>
			<Card.Body>
				{roles.length === 0 && (
					<p className="text-muted">
						No Meta Ads roles assigned yet. The company owner always has Manager
						access.
					</p>
				)}
				{roles.length > 0 && (
					<table className="table">
						<thead>
							<tr>
								<th>User</th>
								<th>Email</th>
								<th>Role</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{roles.map((entry) => (
								<tr key={entry.userId}>
									<td>
										{entry.firstName} {entry.lastName}
									</td>
									<td>{entry.email}</td>
									<td>
										<Select
											size="sm"
											value={entry.role}
											onChange={(e) =>
												setRole.mutate({
													userId: entry.userId,
													role: e.target.value as MetaAdsRole,
												})
											}
											aria-label={`Meta Ads role for ${entry.firstName} ${entry.lastName}`}
										>
											{Object.entries(roleLabels).map(([value, label]) => (
												<option key={value} value={value}>
													{label}
												</option>
											))}
										</Select>
									</td>
									<td>
										<button
											type="button"
											className="btn btn-sm btn-outline-danger"
											onClick={() => removeRole.mutate(entry.userId)}
										>
											<Icon name="delete" size={16} />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				{unassignedUsers.length > 0 && (
					<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
						<Select
							size="sm"
							value={addUserId}
							onChange={(e) =>
								setAddUserId(e.target.value ? Number(e.target.value) : "")
							}
							aria-label="Select user"
						>
							<option value="">Select user...</option>
							{unassignedUsers.map((u) => (
								<option key={u.id} value={u.id}>
									{u.firstName} {u.lastName} ({u.email})
								</option>
							))}
						</Select>
						<Select
							size="sm"
							value={addRole}
							onChange={(e) => setAddRole(e.target.value as MetaAdsRole)}
							aria-label="Select Meta Ads role"
						>
							{Object.entries(roleLabels).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</Select>
						<Button
							size="sm"
							icon="add_circle"
							disabled={!addUserId}
							onClick={() => {
								if (addUserId) {
									setRole.mutate({ userId: Number(addUserId), role: addRole });
									setAddUserId("");
								}
							}}
						>
							Assign
						</Button>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

const MetaAdsRoleManagerGate: FC = () => {
	const canManage = useCanManageMetaAdsRoles();

	if (!canManage) return null;

	return <MetaAdsRoleManagerInner />;
};

export const MetaAdsRoleManager: FC = () => (
	<ErrorBoundary fallback={null}>
		<Suspense fallback={<p className="text-muted">Loading roles...</p>}>
			<MetaAdsRoleManagerGate />
		</Suspense>
	</ErrorBoundary>
);
